import {
  iconInventory,
  iconShopping,
  iconQuickAdd,
  iconHub,
  iconLocations,
  iconRecipes,
  iconRecipeCategories,
  iconIngredients,
  iconTimers,
  iconHistory,
  iconProductMasters,
  iconMapping,
  iconShoppingCategories,
  iconUnits,
  iconUsers,
} from './brand-icons.js';
import { hasCapability } from '../capabilities.js';

/**
 * The single destination registry. Three surfaces project it: the desktop
 * sidebar, the Catalog hub page (`pages/catalog.js`) and the bottom tab bar
 * (`components/bottom-nav.js`). Adding a destination is one entry here.
 *
 * The bottom bar used to keep its own module-local `TABS` array, which is the
 * mechanical reason the mobile and desktop navigations were able to drift into
 * two different information architectures. It now reads `primary: true` from
 * this list instead, so a registry addition can never again reach one surface
 * and miss another.
 *
 * `icon` (emoji) is kept for non-chrome consumers; every chrome surface renders
 * `iconFn` — per brand-iconography, app chrome never falls back to emoji.
 *
 * @typedef {'vorrat'|'kueche'|'auswertung'|'verwaltung'} CatalogGroupKey
 * @typedef {'manage_catalog'|'manage_household'|null} Capability
 *
 * @typedef {{
 *   path: string,       // hash path (e.g. "/locations")
 *   icon: string,       // emoji (legacy/non-chrome use only)
 *   iconFn: (extraClass?: string) => SVGSVGElement, // brand icon used by all chrome
 *   titleKey: string,   // i18n key for the row/tab title
 *   descKey: string,    // i18n key for the one-line description
 *   group: CatalogGroupKey,
 *   requiredCapability: Capability, // null = visible to every household member
 *   primary?: boolean,  // projected into the four-tab bottom nav
 *   emphasis?: 'featured', // reserved for the identity phase; set on no row
 * }} CatalogItem
 *
 * @typedef {{ key: CatalogGroupKey, labelKey: string }} CatalogGroup
 *
 * @type {Array<CatalogItem>}
 */
/**
 * The hub route.
 *
 * The hub is a **surface, not a destination**: it is the sidebar, rendered for
 * a viewport with no room for one. Two consequences follow, and both surfaces
 * already needed the path to express them — the hub omits its own row, and the
 * desktop sidebar omits the hub. Declared once here because three modules had
 * grown their own copy of the literal, which is the same drift that once left
 * the bottom bar and the sidebar showing different information architectures.
 */
export const HUB_PATH = '/catalog';

export const CATALOG_ITEMS = [
  { path: '/',                    icon: '📦', iconFn: iconInventory,          titleKey: 'nav.inventory',             descKey: 'catalog.inventory_desc',          group: 'vorrat',     requiredCapability: null,              primary: true },
  { path: '/shopping',            icon: '🛒', iconFn: iconShopping,           titleKey: 'nav.shopping',              descKey: 'catalog.shopping_desc',           group: 'vorrat',     requiredCapability: null,              primary: true },
  { path: '/quick-add',           icon: '🌱', iconFn: iconQuickAdd,           titleKey: 'nav.quick_add',             descKey: 'catalog.quick_add_desc',          group: 'vorrat',     requiredCapability: null,              primary: true },
  { path: '/catalog',             icon: '🗂️', iconFn: iconHub,                titleKey: 'nav.catalog',               descKey: 'catalog.hub_desc',                group: 'vorrat',     requiredCapability: null,              primary: true },

  { path: '/recipes',             icon: '🍳', iconFn: iconRecipes,            titleKey: 'recipes.title',             descKey: 'catalog.recipes_desc',            group: 'kueche',     requiredCapability: null },
  { path: '/recipe-categories',   icon: '🗂️', iconFn: iconRecipeCategories,   titleKey: 'recipe_categories.title',   descKey: 'catalog.recipe_categories_desc',  group: 'kueche',     requiredCapability: null },
  { path: '/ingredients',         icon: '🥕', iconFn: iconIngredients,        titleKey: 'ingredients.title',         descKey: 'catalog.ingredients_desc',        group: 'kueche',     requiredCapability: null },
  { path: '/timers',              icon: '⏲️', iconFn: iconTimers,             titleKey: 'timers.title',              descKey: 'catalog.timers_desc',             group: 'kueche',     requiredCapability: null },

  { path: '/history',             icon: '🕘', iconFn: iconHistory,            titleKey: 'nav.history',               descKey: 'catalog.history_desc',            group: 'auswertung', requiredCapability: null },

  { path: '/locations',           icon: '📍', iconFn: iconLocations,          titleKey: 'storage.title',             descKey: 'catalog.locations_desc',          group: 'verwaltung', requiredCapability: 'manage_catalog' },
  { path: '/product-masters',     icon: '🏷️', iconFn: iconProductMasters,     titleKey: 'product_masters.tab_title', descKey: 'catalog.product_masters_desc',    group: 'verwaltung', requiredCapability: 'manage_catalog' },
  { path: '/ingredient-mapping',  icon: '🔗', iconFn: iconMapping,            titleKey: 'ingredient_mapping.title',  descKey: 'catalog.ingredient_mapping_desc', group: 'verwaltung', requiredCapability: 'manage_catalog' },
  { path: '/shopping-categories', icon: '🛒', iconFn: iconShoppingCategories, titleKey: 'shopping.categories_title', descKey: 'catalog.shopping_categories_desc', group: 'verwaltung', requiredCapability: 'manage_catalog' },
  { path: '/units',               icon: '⚖️', iconFn: iconUnits,              titleKey: 'units.title',               descKey: 'catalog.units_desc',              group: 'verwaltung', requiredCapability: 'manage_catalog' },
  { path: '/users',               icon: '👥', iconFn: iconUsers,              titleKey: 'users.title',               descKey: 'catalog.users_desc',              group: 'verwaltung', requiredCapability: 'manage_household' },
];

/**
 * Ordered group definitions — a task taxonomy, not a domain taxonomy. Every
 * structural surface derives its sections from this constant.
 *
 * @type {Array<CatalogGroup>}
 */
export const CATALOG_GROUPS = [
  { key: 'vorrat',     labelKey: 'catalog.group.vorrat'     },
  { key: 'kueche',     labelKey: 'catalog.group.kueche'     },
  { key: 'auswertung', labelKey: 'catalog.group.auswertung' },
  { key: 'verwaltung', labelKey: 'catalog.group.verwaltung' },
];

/** Fallback bucket for an entry whose `group` is missing or unrecognised. */
const FALLBACK_GROUP = 'verwaltung';

/**
 * The four destinations the bottom tab bar projects, in registry order.
 * `bottom-nav.js` consumes this instead of declaring its own list.
 *
 * @returns {Array<CatalogItem>}
 */
export function primaryCatalogItems() {
  return CATALOG_ITEMS.filter((item) => item.primary === true);
}

/**
 * Map a hash route to the registry entry that owns it.
 *
 * Sub-routes resolve to their parent by longest-prefix match, which is what
 * makes an active state possible on `/recipes/:id` and `/history/:itemId` —
 * neither is a registry entry, and both are where users spend the most time.
 * `/` is matched exactly so it never swallows every other path.
 *
 * @param {string|null|undefined} path
 * @returns {CatalogItem|null}
 */
export function itemForPath(path) {
  if (typeof path !== 'string' || path === '') return null;

  /** @type {CatalogItem|null} */
  let best = null;
  for (const item of CATALOG_ITEMS) {
    if (item.path === '/') {
      if (path === '/') best = item;
      continue;
    }
    if (path === item.path || path.startsWith(item.path + '/')) {
      if (!best || item.path.length > best.path.length) best = item;
    }
  }
  return best;
}

/**
 * Map a hash route to the key of the group that owns it, so every surface can
 * answer "which section am I in" identically.
 *
 * @param {string|null|undefined} path
 * @returns {CatalogGroupKey|null}
 */
export function groupForPath(path) {
  const item = itemForPath(path);
  return item ? item.group : null;
}

/**
 * Filter CATALOG_ITEMS by what the viewer is allowed to see. Visibility is a
 * per-row property, never a per-group one: a group is shown when at least one
 * of its children is visible. Encoding "Verwaltung is admin-only" as a group
 * property would design out the mixed case a product-manager role creates.
 *
 * @param {string|null} [role]
 * @returns {Array<CatalogItem>}
 */
export function visibleCatalogItems(role) {
  return CATALOG_ITEMS.filter((item) => hasCapability(item.requiredCapability, role));
}

/**
 * Pure helper: bucket an arbitrary array of CatalogItem-shaped objects by group
 * in CATALOG_GROUPS order. Groups with no items are omitted. An entry with a
 * missing or unrecognised `group` falls into `verwaltung` — fail visible, not
 * silent.
 *
 * Exported so tests can feed synthetic entries and exercise the default branch
 * without mutating the module-level CATALOG_ITEMS constant.
 *
 * @param {Array<CatalogItem>} items
 * @returns {Array<{ key: string, labelKey: string, items: Array<CatalogItem> }>}
 */
export function bucketItemsByGroup(items) {
  const validKeys = new Set(CATALOG_GROUPS.map((g) => g.key));

  /** @type {Map<string, Array<CatalogItem>>} */
  const buckets = new Map(CATALOG_GROUPS.map((g) => [g.key, []]));

  for (const item of items) {
    const key = validKeys.has(item.group) ? item.group : FALLBACK_GROUP;
    buckets.get(key).push(item);
  }

  return CATALOG_GROUPS
    .filter((g) => buckets.get(g.key).length > 0)
    .map((g) => ({ key: g.key, labelKey: g.labelKey, items: buckets.get(g.key) }));
}

/**
 * Bucket the viewer-visible items by group in CATALOG_GROUPS order.
 *
 * @param {string|null} [role]
 * @returns {Array<{ key: string, labelKey: string, items: Array<CatalogItem> }>}
 */
export function groupVisibleCatalogItems(role) {
  return bucketItemsByGroup(visibleCatalogItems(role));
}
